/**
 * RESPONSIBILITY: hold the scene showing, the one leaving and the one waiting, and move along on a timer.
 *
 * THE NEXT SCENE IS DRAWN BEFORE IT IS NEEDED, hidden. Its photographs load and decode while
 * the current scene is still showing, so when it fades in nothing is still arriving - a
 * picture that pops in half way through a fade is the roughness this exists to prevent.
 *
 * THE SCENE THAT LEFT STAYS FOR THE FADE and then goes, so the new one dissolves in over it.
 */
import { useEffect, useRef, useState } from 'react';
import { firstScene, nextScene, type Pools, type Scene } from './heroChoose';

/** A little over the dissolve in the stylesheet (1.2s). */
export const FADE_MS = 1400;

interface Args {
  pools: Pools;
  maxGroup: number;
  wait: number;
  cycling: boolean;
}

interface State { cur: Scene; next: Scene; prev?: Scene }

const start = ({ pools, maxGroup }: Args): State => {
  const cur = firstScene(pools);
  return { cur, next: nextScene(cur, pools, maxGroup) };
};

export function useHeroScenes(a: Args) {
  const { pools, maxGroup, wait, cycling } = a;
  const [s, setS] = useState<State>(() => start(a));

  useEffect(() => {
    if (!cycling) return;
    const t = window.setInterval(
      () => setS((x) => ({ prev: x.cur, cur: x.next, next: nextScene(x.next, pools, maxGroup) })),
      wait,
    );
    return () => window.clearInterval(t);
  }, [cycling, pools.singles, pools.groups, maxGroup, wait]);

  useEffect(() => {
    if (!s.prev) return;
    const t = window.setTimeout(() => setS((x) => ({ cur: x.cur, next: x.next })), FADE_MS);
    return () => window.clearTimeout(t);
  }, [s.prev]);

  // The screen crossed a line, or the editor changed the pictures: what was chosen may no
  // longer fit, so start again. Not on the first render, which has just chosen.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setS(start(a));
  }, [pools.singles, pools.groups, maxGroup]);

  return { current: s.cur, leaving: s.prev, next: s.next };
}
